<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import reportingOverridesAPI from 'dashboard/api/reportingOverrides';
import conversationAPI from 'dashboard/api/inbox/conversation';

const { t: i18nT } = useI18n({ useScope: 'global' });

const fallbackMessages = {
  'REPORT.QA_OVERRIDES.HEADER': 'QA Overrides',
  'REPORT.QA_OVERRIDES.DESCRIPTION':
    'Allow QA specialists to exclude conversations from agent performance and reporting metrics.',
  'REPORT.QA_OVERRIDES.CREATE_SECTION.TITLE': 'Add Override',
  'REPORT.QA_OVERRIDES.FORM.CONVERSATION_DISPLAY_ID': 'Conversation ID',
  'REPORT.QA_OVERRIDES.FORM.PLACEHOLDER': 'Enter Conversation Display ID',
  'REPORT.QA_OVERRIDES.FORM.LOAD_CONVERSATION': 'Load',
  'REPORT.QA_OVERRIDES.FORM.REASON': 'Exclusion Reason',
  'REPORT.QA_OVERRIDES.FORM.REASON_PLACEHOLDER':
    'Describe why this conversation should be excluded',
  'REPORT.QA_OVERRIDES.FORM.NOTES': 'Notes (optional)',
  'REPORT.QA_OVERRIDES.FORM.NOTES_PLACEHOLDER':
    'Additional context for reviewers',
  'REPORT.QA_OVERRIDES.FORM.SUBMIT': 'Create Override',
  'REPORT.QA_OVERRIDES.FORM.RESET': 'Clear',
  'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.TITLE': 'Conversation Preview',
  'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.EMPTY':
    'Enter a conversation ID and click Load to preview.',
  'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.CONVERSATION_ID': 'ID',
  'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.CUSTOMER': 'Customer',
  'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.AGENT': 'Agent',
  'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.STATUS': 'Status',
  'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.CREATED_AT': 'Created',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.TITLE': 'Override Records',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_FILTER': 'Status',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_ACTIVE': 'Active',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_REVOKED': 'Revoked',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_ALL': 'All',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.CONVERSATION': 'Conversation',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.AGENT': 'Agent',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.REASON': 'Reason',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.CREATED_AT': 'Created',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS': 'Status',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.EFFECTIVE_FROM': 'Effective from {{time}}',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.REVOKED_AT': 'Revoked at {{time}}',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.REVOKE': 'Revoke',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.LOADING': 'Loading…',
  'REPORT.QA_OVERRIDES.OVERRIDE_LIST.EMPTY': 'No override records found',
  'REPORT.QA_OVERRIDES.ALERTS.FETCH_FAILED': 'Failed to load overrides',
  'REPORT.QA_OVERRIDES.ALERTS.CONVERSATION_NOT_FOUND': 'Conversation not found',
  'REPORT.QA_OVERRIDES.ALERTS.VALIDATION_ERROR':
    'Please enter the conversation ID and reason',
  'REPORT.QA_OVERRIDES.ALERTS.SUBMIT_SUCCESS': 'Override created successfully',
  'REPORT.QA_OVERRIDES.ALERTS.SUBMIT_FAILED': 'Failed to create override',
  'REPORT.QA_OVERRIDES.ALERTS.REVOKE_SUCCESS': 'Override revoked',
  'REPORT.QA_OVERRIDES.ALERTS.REVOKE_FAILED': 'Failed to revoke override',
};

const translate = (key, values = {}) => {
  const translated = i18nT(key, values);
  if (translated && translated !== key) return translated;
  const fallback = fallbackMessages[key];
  if (!fallback) return key;
  if (!values || Object.keys(values).length === 0) return fallback;
  return fallback.replace(/\{\{(\w+)\}\}/g, (_, m) => values[m] ?? '');
};

const overrides = ref([]);
const isLoadingOverrides = ref(false);
const isSubmitting = ref(false);
const statusFilter = ref('approved');

const form = reactive({ conversationDisplayId: '', reason: '', notes: '' });
const conversationPreview = ref(null);
const isFetchingConversation = ref(false);

const hasFormErrors = computed(
  () => !form.conversationDisplayId.trim() || !form.reason.trim()
);
const hasConversationLoaded = computed(() =>
  Boolean(conversationPreview.value)
);

const fetchOverrides = async () => {
  isLoadingOverrides.value = true;
  try {
    const { data } = await reportingOverridesAPI.get({
      status: statusFilter.value,
    });
    overrides.value = data;
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        translate('REPORT.QA_OVERRIDES.ALERTS.FETCH_FAILED')
    );
  } finally {
    isLoadingOverrides.value = false;
  }
};

const resetForm = () => {
  form.conversationDisplayId = '';
  form.reason = '';
  form.notes = '';
  conversationPreview.value = null;
};

const fetchConversation = async () => {
  if (!form.conversationDisplayId.trim()) {
    conversationPreview.value = null;
    return;
  }
  isFetchingConversation.value = true;
  try {
    const { data } = await conversationAPI.show(
      form.conversationDisplayId.trim()
    );
    conversationPreview.value = data;
  } catch (error) {
    conversationPreview.value = null;
    useAlert(
      error?.response?.data?.message ||
        translate('REPORT.QA_OVERRIDES.ALERTS.CONVERSATION_NOT_FOUND')
    );
  } finally {
    isFetchingConversation.value = false;
  }
};

const submitOverride = async () => {
  if (hasFormErrors.value) {
    useAlert(translate('REPORT.QA_OVERRIDES.ALERTS.VALIDATION_ERROR'));
    return;
  }
  isSubmitting.value = true;
  try {
    await reportingOverridesAPI.create({
      conversation_display_id: form.conversationDisplayId.trim(),
      reason: form.reason.trim(),
      notes: form.notes.trim() || null,
    });
    useAlert(translate('REPORT.QA_OVERRIDES.ALERTS.SUBMIT_SUCCESS'));
    resetForm();
    await fetchOverrides();
  } catch (error) {
    const msg =
      error?.response?.data?.errors?.join(', ') ||
      error?.response?.data?.message ||
      error.message;
    useAlert(msg || translate('REPORT.QA_OVERRIDES.ALERTS.SUBMIT_FAILED'));
  } finally {
    isSubmitting.value = false;
  }
};

const revokeOverride = async id => {
  try {
    await reportingOverridesAPI.revoke(id);
    useAlert(translate('REPORT.QA_OVERRIDES.ALERTS.REVOKE_SUCCESS'));
    await fetchOverrides();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        translate('REPORT.QA_OVERRIDES.ALERTS.REVOKE_FAILED')
    );
  }
};

const formattedOverrides = computed(() =>
  overrides.value.map(item => ({
    ...item,
    created_at: item.created_at
      ? new Date(item.created_at).toLocaleString()
      : '--',
    revoked_at: item.revoked_at
      ? new Date(item.revoked_at).toLocaleString()
      : null,
    effective_from: item.effective_from
      ? new Date(item.effective_from).toLocaleString()
      : '--',
  }))
);

onMounted(fetchOverrides);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="qa-page">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <button
            class="qa-side-toggle"
            type="button"
            aria-label="Toggle side navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 4v16M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
              />
            </svg>
          </button>
          <h1 class="qa-title">
            {{ translate('REPORT.QA_OVERRIDES.HEADER') }}
          </h1>
        </div>
        <div class="qa-header-meta">
          {{ translate('REPORT.QA_OVERRIDES.DESCRIPTION') }}
        </div>
      </div>

      <div class="qa-content">
        <section class="qa-card">
          <div class="qa-card-header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="20"
              height="20"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              />
            </svg>
            <span>{{
              translate('REPORT.QA_OVERRIDES.CREATE_SECTION.TITLE')
            }}</span>
          </div>

          <div class="qa-card-body">
            <div class="qa-form-grid">
              <div class="qa-form-col">
                <div class="qa-field">
                  <label class="qa-label">
                    {{
                      translate(
                        'REPORT.QA_OVERRIDES.FORM.CONVERSATION_DISPLAY_ID'
                      )
                    }}
                    <span class="qa-req">*</span>
                  </label>
                  <div class="qa-input-row">
                    <input
                      v-model="form.conversationDisplayId"
                      type="text"
                      class="qa-input"
                      :placeholder="
                        translate('REPORT.QA_OVERRIDES.FORM.PLACEHOLDER')
                      "
                    />
                    <button
                      class="qa-load-btn"
                      type="button"
                      :disabled="isFetchingConversation"
                      @click="fetchConversation"
                    >
                      {{
                        isFetchingConversation
                          ? 'Loading…'
                          : translate(
                              'REPORT.QA_OVERRIDES.FORM.LOAD_CONVERSATION'
                            )
                      }}
                    </button>
                  </div>
                </div>

                <div class="qa-field">
                  <label class="qa-label">
                    {{ translate('REPORT.QA_OVERRIDES.FORM.REASON') }}
                    <span class="qa-req">*</span>
                  </label>
                  <textarea
                    v-model="form.reason"
                    rows="3"
                    class="qa-textarea"
                    :placeholder="
                      translate('REPORT.QA_OVERRIDES.FORM.REASON_PLACEHOLDER')
                    "
                  />
                </div>

                <div class="qa-field">
                  <label class="qa-label">{{
                    translate('REPORT.QA_OVERRIDES.FORM.NOTES')
                  }}</label>
                  <textarea
                    v-model="form.notes"
                    rows="2"
                    class="qa-textarea"
                    :placeholder="
                      translate('REPORT.QA_OVERRIDES.FORM.NOTES_PLACEHOLDER')
                    "
                  />
                </div>

                <div class="qa-form-actions">
                  <button
                    class="qa-submit-btn"
                    type="button"
                    :disabled="hasFormErrors || isSubmitting"
                    @click="submitOverride"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                    >
                      <path
                        stroke="currentcolor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="m5 13 4 4L19 7"
                      />
                    </svg>
                    <span>{{
                      isSubmitting
                        ? 'Creating…'
                        : translate('REPORT.QA_OVERRIDES.FORM.SUBMIT')
                    }}</span>
                  </button>
                  <button
                    class="qa-reset-btn"
                    type="button"
                    :disabled="isSubmitting"
                    @click="resetForm"
                  >
                    {{ translate('REPORT.QA_OVERRIDES.FORM.RESET') }}
                  </button>
                </div>
              </div>

              <div class="qa-preview-col">
                <div
                  class="qa-preview-card"
                  :class="{ 'qa-preview-card--loaded': hasConversationLoaded }"
                >
                  <div class="qa-preview-title">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 9h8M8 13h6m-4 9-4-4H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-3l-4 4z"
                      />
                    </svg>
                    {{
                      translate(
                        'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.TITLE'
                      )
                    }}
                  </div>
                  <p v-if="!hasConversationLoaded" class="qa-preview-empty">
                    {{
                      translate(
                        'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.EMPTY'
                      )
                    }}
                  </p>
                  <div v-else class="qa-preview-body">
                    <div class="qa-preview-row">
                      <span class="qa-preview-key">{{
                        translate(
                          'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.CONVERSATION_ID'
                        )
                      }}</span>
                      <span class="qa-preview-val">#{{ conversationPreview.id }}</span>
                    </div>
                    <div
                      v-if="conversationPreview.meta?.sender?.name"
                      class="qa-preview-row"
                    >
                      <span class="qa-preview-key">{{
                        translate(
                          'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.CUSTOMER'
                        )
                      }}</span>
                      <span class="qa-preview-val">{{
                        conversationPreview.meta.sender.name
                      }}</span>
                    </div>
                    <div
                      v-if="conversationPreview.meta?.assignee?.name"
                      class="qa-preview-row"
                    >
                      <span class="qa-preview-key">{{
                        translate(
                          'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.AGENT'
                        )
                      }}</span>
                      <span class="qa-preview-val">{{
                        conversationPreview.meta.assignee.name
                      }}</span>
                    </div>
                    <div class="qa-preview-row">
                      <span class="qa-preview-key">{{
                        translate(
                          'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.STATUS'
                        )
                      }}</span>
                      <span
                        class="qa-preview-val qa-preview-status"
                        :class="`qa-status--${conversationPreview.status}`"
                      >
                        {{ conversationPreview.status }}
                      </span>
                    </div>
                    <div class="qa-preview-row">
                      <span class="qa-preview-key">{{
                        translate(
                          'REPORT.QA_OVERRIDES.CONVERSATION_PREVIEW.CREATED_AT'
                        )
                      }}</span>
                      <span class="qa-preview-val">{{
                        new Date(
                          conversationPreview.created_at * 1000
                        ).toLocaleString()
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="20"
              height="20"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 7h18M3 12h18M3 17h18"
              />
            </svg>
            <span>{{
              translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.TITLE')
            }}</span>
            <div class="qa-list-status-filter">
              <label class="qa-filter-label">{{
                translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_FILTER')
              }}</label>
              <select
                v-model="statusFilter"
                class="qa-filter-select"
                @change="fetchOverrides"
              >
                <option value="approved">
                  {{
                    translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_ACTIVE')
                  }}
                </option>
                <option value="revoked">
                  {{
                    translate(
                      'REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_REVOKED'
                    )
                  }}
                </option>
                <option value="">
                  {{
                    translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_ALL')
                  }}
                </option>
              </select>
            </div>
          </div>

          <div class="qa-table-wrap">
            <table class="qa-tbl">
              <thead>
                <tr>
                  <th class="qa-th qa-th--left">
                    {{
                      translate(
                        'REPORT.QA_OVERRIDES.OVERRIDE_LIST.CONVERSATION'
                      )
                    }}
                  </th>
                  <th class="qa-th qa-th--left">
                    {{ translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.AGENT') }}
                  </th>
                  <th class="qa-th qa-th--left">
                    {{ translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.REASON') }}
                  </th>
                  <th class="qa-th qa-th--left">
                    {{
                      translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.CREATED_AT')
                    }}
                  </th>
                  <th class="qa-th">
                    {{ translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS') }}
                  </th>
                  <th class="qa-th" />
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoadingOverrides">
                  <td colspan="6" class="qa-td qa-td--empty">
                    <div class="qa-spinner" />
                  </td>
                </tr>
                <tr v-else-if="!formattedOverrides.length">
                  <td colspan="6" class="qa-td qa-td--empty">
                    {{ translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.EMPTY') }}
                  </td>
                </tr>
                <tr
                  v-for="item in formattedOverrides"
                  :key="item.id"
                  class="qa-row"
                >
                  <td class="qa-td">
                    <div class="qa-conv-id">
                      #{{
                        item.conversation_display_id || item.conversation_id
                      }}
                    </div>
                    <div v-if="item.scope" class="qa-conv-scope">
                      {{ item.scope }}
                    </div>
                  </td>
                  <td class="qa-td">{{ item.agent?.name || '—' }}</td>
                  <td class="qa-td">
                    <div class="qa-reason">{{ item.reason }}</div>
                    <div v-if="item.notes" class="qa-notes">
                      {{ item.notes }}
                    </div>
                  </td>
                  <td class="qa-td">
                    <div>{{ item.created_at }}</div>
                    <div class="qa-time-sub">
                      {{
                        translate(
                          'REPORT.QA_OVERRIDES.OVERRIDE_LIST.EFFECTIVE_FROM',
                          { time: item.effective_from }
                        )
                      }}
                    </div>
                    <div v-if="item.revoked_at" class="qa-time-sub">
                      {{
                        translate(
                          'REPORT.QA_OVERRIDES.OVERRIDE_LIST.REVOKED_AT',
                          { time: item.revoked_at }
                        )
                      }}
                    </div>
                  </td>
                  <td class="qa-td qa-td--center">
                    <span
                      class="qa-status-badge"
                      :class="
                        item.status === 'approved'
                          ? 'qa-status-badge--green'
                          : 'qa-status-badge--gray'
                      "
                    >
                      {{
                        item.status === 'approved'
                          ? translate(
                              'REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_ACTIVE'
                            )
                          : translate(
                              'REPORT.QA_OVERRIDES.OVERRIDE_LIST.STATUS_REVOKED'
                            )
                      }}
                    </span>
                  </td>
                  <td class="qa-td qa-td--center">
                    <button
                      v-if="item.status === 'approved'"
                      class="qa-revoke-btn"
                      type="button"
                      @click="revokeOverride(item.id)"
                    >
                      {{
                        translate('REPORT.QA_OVERRIDES.OVERRIDE_LIST.REVOKE')
                      }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.qa-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.qa-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

/* ── Header ── */
.qa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.qa-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qa-side-toggle {
  width: 40px;
  height: 40px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.qa-side-toggle:hover {
  background: #f3f4f6;
}

.qa-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
  white-space: nowrap;
}

.qa-header-meta {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  max-width: 480px;
  text-align: right;
}

/* ── Content ── */
.qa-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Cards ── */
.qa-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.qa-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e8;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.qa-list-status-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.qa-filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.qa-filter-select {
  height: 32px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  font-size: 13px;
  padding: 0 8px;
  cursor: pointer;
}

.qa-card-body {
  padding: 20px;
}

/* ── Form grid ── */
.qa-form-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 20px;
  align-items: start;
}

.qa-form-col {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.qa-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.qa-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.qa-req {
  color: #dc2626;
  margin-left: 2px;
}

.qa-input-row {
  display: flex;
  gap: 8px;
}

.qa-input {
  flex: 1;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  font-size: 14px;
  padding: 0 12px;
}

.qa-input:focus {
  outline: none;
  border-color: #0076f5;
  box-shadow: 0 0 0 3px rgba(0, 118, 245, 0.12);
}

.qa-textarea {
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  font-size: 14px;
  padding: 10px 12px;
  resize: vertical;
  font-family: inherit;
}

.qa-textarea:focus {
  outline: none;
  border-color: #0076f5;
  box-shadow: 0 0 0 3px rgba(0, 118, 245, 0.12);
}

.qa-load-btn {
  height: 38px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.qa-load-btn:hover {
  background: #f9fafb;
}
.qa-load-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qa-form-actions {
  display: flex;
  gap: 8px;
  padding-top: 4px;
}

.qa-submit-btn {
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #111827;
  color: #ffffff;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.qa-submit-btn:hover:not(:disabled) {
  background: #1f2937;
}
.qa-submit-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.qa-reset-btn {
  height: 40px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.qa-reset-btn:hover:not(:disabled) {
  background: #f9fafb;
}
.qa-reset-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Preview panel ── */
.qa-preview-card {
  border-radius: 8px;
  border: 1px dashed #d1d5db;
  background: #f9fafb;
  padding: 16px;
  min-height: 200px;
}

.qa-preview-card--loaded {
  border: 1px solid #d8dee5;
  background: #ffffff;
}

.qa-preview-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}

.qa-preview-empty {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.5;
}

.qa-preview-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qa-preview-row {
  display: flex;
  gap: 8px;
  font-size: 13px;
}

.qa-preview-key {
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
  min-width: 80px;
}

.qa-preview-val {
  color: #111827;
}

.qa-preview-status {
  text-transform: capitalize;
}
.qa-status--resolved {
  color: #059669;
  font-weight: 600;
}
.qa-status--open {
  color: #0076f5;
  font-weight: 600;
}

/* ── Table ── */
.qa-table-wrap {
  overflow-x: auto;
}

.qa-tbl {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  font-size: 13px;
}

.qa-th {
  padding: 10px 16px;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  text-transform: none;
  text-align: center;
}

.qa-th--left {
  text-align: left;
}

.qa-td {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  vertical-align: middle;
  font-size: 13px;
}

.qa-td--center {
  text-align: center;
}
.qa-td--empty {
  text-align: center;
  padding: 32px;
  color: #6b7280;
}

.qa-row:last-child .qa-td {
  border-bottom: 0;
}
.qa-row:hover .qa-td {
  background: #fafafa;
}

.qa-conv-id {
  font-weight: 700;
  color: #111827;
}
.qa-conv-scope {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}
.qa-reason {
  font-weight: 500;
  color: #111827;
}
.qa-notes {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}
.qa-time-sub {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.qa-status-badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.qa-status-badge--green {
  background: #dcfce7;
  color: #15803d;
}
.qa-status-badge--gray {
  background: #f3f4f6;
  color: #6b7280;
}

.qa-revoke-btn {
  height: 30px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.qa-revoke-btn:hover {
  background: #fee2e2;
}

.qa-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: qa-spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes qa-spin {
  to {
    transform: rotate(360deg);
  }
}

::global(body.dark) .qa-page,
::global(body.dark) .qa-shell,
::global(body.dark) .qa-header,
::global(body.dark) .qa-card,
::global(body.theme-mix) .qa-page,
::global(body.theme-mix) .qa-shell,
::global(body.theme-mix) .qa-header,
::global(body.theme-mix) .qa-card {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .qa-content {
    padding: 12px 16px 16px;
  }
  .qa-form-grid {
    grid-template-columns: 1fr;
  }
  .qa-header-meta {
    display: none;
  }
}
</style>
